/**
 * The Account popup (ACC-008, owner 2026-09-27): its own button in the header, beside
 * Settings, so signing in is one tap rather than a trip through Settings. It holds the
 * Account section (ACC-005), which used to sit at the top of Settings.
 *
 * The same native `<dialog>` pattern as Settings: focus trapping, Esc and the backdrop
 * close it. Who is signed in is asked afresh each time it opens.
 */
import { ACCOUNT_COPY } from '../config/site.ts';
import { createAccountSection, type AccountActions, type AccountState } from './account-section.ts';

export interface AccountDialog {
  open(): void;
  /** Ask the server who is signed in (the header button shows it), without opening. */
  refresh(): Promise<void>;
  /** A one-off message for the next time it shows (a sign-in that did not finish). */
  notice(text: string): void;
}

export function createAccountDialog(
  host: HTMLElement,
  actions: AccountActions,
  onState: (state: AccountState) => void,
): AccountDialog {
  const dialog = document.createElement('dialog');
  dialog.className = 'settings account-dialog';
  dialog.setAttribute('aria-labelledby', 'account-title');
  dialog.innerHTML = `
    <form method="dialog" class="settings__card">
      <header class="settings__header">
        <h2 class="settings__title" id="account-title"></h2>
        <button type="submit" class="btn btn--icon" data-close>&times;</button>
      </header>
      <section class="settings__section settings__account" data-account></section>
    </form>
  `;
  const title = dialog.querySelector('#account-title');
  const close = dialog.querySelector('[data-close]');
  const body = dialog.querySelector<HTMLElement>('[data-account]');
  if (title === null || close === null || body === null) {
    throw new Error('account dialog: its markup is missing a part');
  }
  // Copy as text, never markup (CLAUDE.md §6).
  title.textContent = ACCOUNT_COPY.heading;
  close.setAttribute('aria-label', ACCOUNT_COPY.close);
  host.append(dialog);

  const section = createAccountSection(body, actions, onState);

  // A click on the backdrop (the dialog itself, outside the card) closes it.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  return {
    open() {
      void section.refresh();
      dialog.showModal();
    },
    refresh: () => section.refresh(),
    notice(text) {
      section.notice(text);
    },
  };
}
